/**
 * Which layout tier the screen is in.
 *
 * The COP is a different product at each size. A phone is a full-screen map, a
 * bottom tab bar and sheets. A tablet keeps the desktop header but has room for
 * one rail at a time, so the rails become drawers. A desktop shows both rails
 * and the dock. A wall display has room to spare and widens the rails rather
 * than leaving a sea of empty map between two 300px columns.
 *
 * The tier is published on <body data-viewport> by the shell, so CSS (and the
 * phone:/tablet:/wide: Tailwind variants) can reflow without every component
 * reading this hook.
 */

import { useSyncExternalStore } from 'react';

export type ViewportTier = 'phone' | 'tablet' | 'desktop' | 'wide';

export const PHONE_MAX_WIDTH = 767;
export const TABLET_MAX_WIDTH = 1279;
export const DESKTOP_MAX_WIDTH = 1919;
/** A landscape phone is 844 or more wide, which is tablet width. What gives it
 *  away is the height: with a top bar, a dock and a timeline the desktop chrome
 *  leaves under 100px of map at 390 tall. */
export const LANDSCAPE_PHONE_MAX_HEIGHT = 520;

/** Manual override, so a user (or QA) can force a layout when auto-detection
 *  is wrong: a tablet in a truck, a phone forced to desktop. */
const OVERRIDE_KEY = 'parallax.viewport';

export interface ViewportMetrics {
  width: number;
  height: number;
  /** Primary pointer is a finger, not a mouse. */
  coarse: boolean;
}

export function tierFor({ width, height, coarse }: ViewportMetrics): ViewportTier {
  if (width <= PHONE_MAX_WIDTH) return 'phone';
  // Only on a touch screen: a desktop browser window dragged short is still
  // driven by a mouse and keeps the desktop chrome.
  if (coarse && height <= LANDSCAPE_PHONE_MAX_HEIGHT) return 'phone';
  if (width <= TABLET_MAX_WIDTH) return 'tablet';
  if (width <= DESKTOP_MAX_WIDTH) return 'desktop';
  return 'wide';
}

/** Reads the stored override. 'mobile' is the value the old phone/desktop
 *  switch wrote, so a device that already had it set keeps its layout. */
export function parseOverride(value: string | null): ViewportTier | null {
  if (value === 'mobile' || value === 'phone') return 'phone';
  if (value === 'tablet' || value === 'desktop' || value === 'wide') return value;
  return null;
}

function forced(): ViewportTier | null {
  // Reading window.localStorage THROWS (not returns null) in a locked-down
  // webview or private mode, exactly the environments an edge device runs in.
  // The override is a nicety; never let it take down the layout hook.
  try {
    if (typeof window === 'undefined') return null;
    return parseOverride(window.localStorage.getItem(OVERRIDE_KEY));
  } catch {
    return null;
  }
}

function isCoarse(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(pointer: coarse)').matches
    : false;
}

function subscribe(callback: () => void): () => void {
  if (typeof window === 'undefined') return () => {};
  const mq = typeof window.matchMedia === 'function' ? window.matchMedia('(pointer: coarse)') : null;
  window.addEventListener('resize', callback);
  window.addEventListener('orientationchange', callback);
  window.addEventListener('storage', callback);
  mq?.addEventListener?.('change', callback);
  return () => {
    window.removeEventListener('resize', callback);
    window.removeEventListener('orientationchange', callback);
    window.removeEventListener('storage', callback);
    mq?.removeEventListener?.('change', callback);
  };
}

export function currentViewport(): ViewportTier {
  const override = forced();
  if (override) return override;
  if (typeof window === 'undefined') return 'desktop';
  return tierFor({ width: window.innerWidth, height: window.innerHeight, coarse: isCoarse() });
}

export function useViewport(): ViewportTier {
  return useSyncExternalStore(subscribe, currentViewport, () => 'desktop');
}

/** At least `px` wide, for the odd screen whose own columns need a finer cut
 *  than the four tiers give it. */
export function useMinWidth(px: number): boolean {
  return useSyncExternalStore(
    subscribe,
    () => (typeof window === 'undefined' ? true : window.innerWidth >= px),
    () => true,
  );
}

/** Set or clear the manual layout override. Fires a storage event for same-tab. */
export function setViewportOverride(mode: ViewportTier | 'mobile' | null): void {
  try {
    if (mode === null) window.localStorage.removeItem(OVERRIDE_KEY);
    else window.localStorage.setItem(OVERRIDE_KEY, mode);
  } catch {
    /* storage unavailable: the measured size still drives the layout */
  }
  window.dispatchEvent(new StorageEvent('storage', { key: OVERRIDE_KEY }));
}
